// re.rejouis.fr — LOT : jouets sans référence connue (ou plusieurs), estimés à la
// main par l'atelier. Depuis le 2026-10-13 le lot est une LIGNE DU PANIER : il part
// dans la même demande (et le même colis) que les jouets reconnus. L'atelier fait
// une offre sous 14 jours ; le client l'accepte ou retire le lot, puis reçoit son
// étiquette (cf. lab _lot.js). Un seul lot par panier.
import { useRef, useState } from 'react';
import { EcranHeader, Icons, Spinner, Sticker, Illus } from '../components/ui.jsx';

const LOT_MIN = 1; // dès 1 jouet depuis le 2026-10-28 (était 2)

const ETAPES_LOT = [
  { illus: 'lot', t: 'Tu l’ajoutes à ton panier', s: 'Avec tes autres jouets si tu en as : une seule demande, un seul colis.' },
  { illus: 'verifie', t: 'On t’envoie une offre', s: 'Sous 14 jours maximum. Tu l’acceptes, ou tu retires le lot.' },
  { illus: 'camion', t: 'Tu reçois ton étiquette', s: 'Mondial Relay, offerte, dès ta réponse.' },
];

// Compresse une image en JPEG ≤ ~200 Ko via Canvas.
function compresserImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX = 900;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round((height * MAX) / width); width = MAX; }
          else { width = Math.round((width * MAX) / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const b64 = canvas.toDataURL('image/jpeg', 0.75).split(',')[1];
        resolve({ b64, mime: 'image/jpeg', nom: file.name });
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ── ÉTAPE 1 : Intro ──────────────────────────────────────────────────────────
function EtapeIntro({ onSuivant, onDon }) {
  return (
    <div style={{ padding: '8px 18px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0 20px' }}>
        <Sticker nom="boite" size={110} anim="pop" />
      </div>
      <h2 className="t-display" style={{ fontSize: 26, margin: '0 0 10px', textAlign: 'center' }}>Tu as un lot de jouets ?</h2>
      <p style={{ fontSize: 13.5, color: 'var(--stone)', lineHeight: 1.65, textAlign: 'center', margin: '0 0 28px' }}>
        Pour les jouets <strong>introuvables</strong> dans notre catalogue, même un seul : on les évalue à la main et on te propose un crédit.
      </p>

      <div className="t-overline" style={{ marginBottom: 10 }}>Le lot, comment ça marche</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
        {ETAPES_LOT.map(({ illus, t, s }, i) => (
          <div key={i} className="card" style={{ display: 'flex', gap: 12, padding: 14, alignItems: 'center' }}>
            <Illus nom={illus} size={42} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t}</div>
              <div style={{ fontSize: 12, color: 'var(--stone)', lineHeight: 1.45 }}>{s}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="soft-card" style={{ padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 24 }}>
        <Icons.info size={16} style={{ color: 'var(--stone)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12.5, color: 'var(--stone)', lineHeight: 1.5 }}>
          On ne reprend que les jouets <strong>fonctionnels</strong> et <strong>propres</strong>. Certaines matières et certains modèles ne sont pas éligibles : on vérifie ça à réception.
        </div>
      </div>

      {/* Beaucoup veulent surtout se débarrasser de petits jouets : le don évite d'attendre une offre. */}
      <div className="soft-card" style={{ padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 24 }}>
        <Illus nom="don" size={34} />
        <div style={{ fontSize: 12.5, color: 'var(--stone)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--noir)' }}>Surtout envie de faire de la place&nbsp;?</strong> Le don, c’est sans attente.{' '}
          <button onClick={onDon} style={{ color: 'var(--rouge)', fontWeight: 600, fontSize: 12.5, padding: 0 }}>Faire un don</button>
        </div>
      </div>

      <button className="btn btn-primary btn-block" onClick={onSuivant}>
        Mes jouets sont introuvables : décrire mon lot <Icons.chev size={16} />
      </button>
    </div>
  );
}

// ── ÉTAPE 2 : Description du lot + photos ───────────────────────────────────
function EtapeDescription({ quantite, setQuantite, notes, setNotes, photos, setPhotos, onSuivant, onPrecedent, dejaAuPanier }) {
  const [erreurNotes, setErreurNotes] = useState('');
  const [erreurPhotos, setErreurPhotos] = useState('');
  const [chargementPhotos, setChargementPhotos] = useState(false);
  const inputRef = useRef(null);

  const nbJouets = parseInt(quantite, 10) || 0;
  function setNb(n) { setQuantite(String(Math.max(0, n))); }

  function valider() {
    if (nbJouets < LOT_MIN) return;
    const notesOk = notes.trim().length > 0;
    const photosOk = photos.length > 0;
    setErreurNotes(notesOk ? '' : 'Décris ton lot en quelques mots.');
    setErreurPhotos(photosOk ? '' : 'Ajoute au moins une photo du lot.');
    if (!notesOk || !photosOk) return;
    onSuivant();
  }

  async function ajouterPhotos(e) {
    const fichiers = Array.from(e.target.files || []);
    if (!fichiers.length) return;
    const restant = 3 - photos.length;
    setChargementPhotos(true);
    try {
      const nouvelles = await Promise.all(fichiers.slice(0, restant).map(compresserImage));
      setPhotos((p) => [...p, ...nouvelles].slice(0, 3));
      setErreurPhotos('');
    } finally {
      setChargementPhotos(false);
      e.target.value = '';
    }
  }

  function retirerPhoto(i) {
    setPhotos((p) => p.filter((_, idx) => idx !== i));
  }

  return (
    <div style={{ padding: '8px 18px 32px' }}>
      <div className="t-overline" style={{ marginBottom: 10 }}>Ton lot</div>

      {/* Quantité exacte */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>Combien de jouets exactement ?</div>
        <div style={{ fontSize: 12, color: 'var(--stone)', marginBottom: 10, lineHeight: 1.5 }}>
          Compte le nombre précis de jouets que tu nous envoies.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setNb(nbJouets - 1)} disabled={nbJouets <= 0} aria-label="Moins"
            style={{ width: 46, height: 46, borderRadius: 12, border: '1.5px solid var(--bord)', background: 'var(--blanc)',
              fontSize: 22, fontWeight: 600, color: 'var(--noir)', flexShrink: 0, lineHeight: 1 }}>−</button>
          <input
            type="number" inputMode="numeric" min={1} placeholder="0"
            value={quantite}
            onChange={(e) => setQuantite(e.target.value.replace(/[^\d]/g, ''))}
            style={{ flex: 1, textAlign: 'center', padding: '12px 8px', borderRadius: 12, border: '1.5px solid var(--bord)',
              fontSize: 22, fontWeight: 700, outline: 'none', boxSizing: 'border-box', background: 'var(--blanc)',
              color: 'var(--noir)', minWidth: 0 }}
          />
          <button onClick={() => setNb(nbJouets + 1)} aria-label="Plus"
            style={{ width: 46, height: 46, borderRadius: 12, border: '1.5px solid var(--bord)', background: 'var(--blanc)',
              fontSize: 22, fontWeight: 600, color: 'var(--noir)', flexShrink: 0, lineHeight: 1 }}>+</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 8 }}>Dès 1 jouet : chacun est évalué à la main par notre atelier.</div>
      </div>

      {/* Notes libres */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13.5, fontWeight: 600, display: 'block', marginBottom: 8 }}>
          Décris ton lot
        </label>
        <textarea
          placeholder="Marques, catégories, état général… tout ce qui peut nous aider à évaluer."
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setErreurNotes(''); }}
          rows={3}
          style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: erreurNotes ? '1.5px solid var(--rouge)' : '1.5px solid var(--bord)',
            fontSize: 14, resize: 'vertical', outline: 'none', boxSizing: 'border-box',
            fontFamily: 'inherit', background: 'var(--blanc)', lineHeight: 1.5 }}
        />
        {erreurNotes && <div style={{ fontSize: 12, color: 'var(--rouge)', marginTop: 5 }}>{erreurNotes}</div>}
      </div>

      {/* Photos */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
          Photos du lot <span style={{ fontWeight: 400, color: 'var(--stone)' }}>(1 à 3)</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--stone)', marginBottom: 10, lineHeight: 1.5 }}>
          Une ou deux photos de l’ensemble pour qu’on puisse évaluer ton lot.
        </div>
        {/* On évalue l'état réel : une photo produit trouvée en ligne ne nous apprend rien. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', marginBottom: 12, borderRadius: 10,
          background: 'var(--jaune-pale)', fontSize: 12.5, color: 'var(--encre)', lineHeight: 1.5 }}>
          <Icons.camera size={16} style={{ flexShrink: 0, marginTop: 1, color: 'var(--rouge)' }} />
          <span>
            <strong>Prends tes jouets en photo toi-même, tels qu’ils sont aujourd’hui.</strong>{' '}
            Pas de photo produit trouvée sur internet : on évalue l’état réel de ton lot (usure, marques, accessoires).
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {photos.map((ph, i) => (
            <div key={i} style={{ position: 'relative', width: 80, height: 80, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
              <img src={`data:${ph.mime};base64,${ph.b64}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <button onClick={() => retirerPhoto(i)}
                style={{ position: 'absolute', top: 3, right: 3, width: 22, height: 22, borderRadius: 999, background: 'rgba(0,0,0,0.55)',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', padding: 0 }}>
                <Icons.x size={12} sw={2.5} />
              </button>
            </div>
          ))}

          {photos.length < 3 && (
            <button onClick={() => inputRef.current?.click()} disabled={chargementPhotos}
              style={{ width: 80, height: 80, borderRadius: 10, border: erreurPhotos ? '1.5px dashed var(--rouge)' : '1.5px dashed var(--bord)', background: 'var(--creme-fonce)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                color: 'var(--stone)', fontSize: 11, fontWeight: 600 }}>
              {chargementPhotos ? <Spinner size={18} /> : <><Icons.camera size={22} style={{ color: 'var(--rouge)' }} />Ajouter</>}
            </button>
          )}
        </div>
        {erreurPhotos && <div style={{ fontSize: 12, color: 'var(--rouge)', marginTop: 5 }}>{erreurPhotos}</div>}

        <input ref={inputRef} type="file" accept="image/*" multiple capture="environment"
          style={{ display: 'none' }} onChange={ajouterPhotos} />
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onPrecedent}>Retour</button>
        <button className="btn btn-primary" style={{ flex: 2 }} disabled={nbJouets < LOT_MIN} onClick={valider}>
          {dejaAuPanier ? 'Mettre à jour mon lot' : 'Ajouter au panier'} <Icons.chev size={16} />
        </button>
      </div>
    </div>
  );
}

// ── ÉCRAN PRINCIPAL ───────────────────────────────────────────────────────────
export default function Lot({ go, panier = [], onAjouter }) {
  const existant = panier.find((l) => l.lot) || null;
  const [etape, setEtape] = useState(existant ? 'description' : 'intro'); // intro | description
  const [quantite, setQuantite] = useState(existant ? String(existant.quantite) : '');
  const [notes, setNotes] = useState(existant?.notes || '');
  const [photos, setPhotos] = useState(existant?.photos || []);

  function ajouter() {
    const n = Math.max(LOT_MIN, parseInt(quantite, 10) || LOT_MIN);
    onAjouter({
      produitId: 'lot',
      lot: true,
      titre: `Lot de ${n} jouet${n > 1 ? 's' : ''}`,
      quantite: n,
      notes: notes.trim(),
      photos,
      image: photos[0] ? `data:${photos[0].mime};base64,${photos[0].b64}` : null,
      credit: 0, // offre de l'atelier sous 14 jours
    });
  }

  return (
    <div className="no-scrollbar" style={{ height: '100%', overflow: 'auto', paddingBottom: 40 }}>
      <EcranHeader titre={etape === 'intro' ? 'J’ai un lot' : 'Mon lot'} onBack={() => {
        if (etape === 'intro' || existant) go(existant ? 'panier' : 'accueil');
        else setEtape('intro');
      }} />

      {etape === 'intro' && (
        <EtapeIntro onSuivant={() => setEtape('description')} onDon={() => go('don')} />
      )}
      {etape === 'description' && (
        <EtapeDescription
          quantite={quantite} setQuantite={setQuantite}
          notes={notes} setNotes={setNotes}
          photos={photos} setPhotos={setPhotos}
          onSuivant={ajouter}
          onPrecedent={() => (existant ? go('panier') : setEtape('intro'))}
          dejaAuPanier={Boolean(existant)}
        />
      )}
    </div>
  );
}
